// @ts-nocheck
import { assign } from 'xstate';
import logger from '../../logger';
import { createOfflineFirstService } from '../actors/offlineFirstActor';
import { fetchChromeModelsFromCloud } from '../cloud/chromeModels';
import { checkUserSettingsLocalDatabaseContent } from '../offline/userSettingsHelpers';
import { fetchUserSettingsFromCloud } from '../cloud/userSettings';
import { checkAreasOfFocusCollectionsLocalDatabaseContent } from '../offline/areasOfFocusCollectionsHelpers';
import { fetchAreasOfFocusCollectionsFromCloud } from '../cloud/areasOfFocusCollections';
import { fetchNotesFromCloudPaginated } from '../cloud/notes';
import { fetchNotebooksFromCloud } from '../cloud/notebooks';
import { fetchUserPanelConfigsFromCloud } from '../cloud/userPanelConfigs';
import { checkNotesLocalDatabaseContent } from '../offline/noteHelpers';
import { checkNotebooksLocalDatabaseContent } from '../offline/notebookHelpers';
import { checkUserPanelConfigsLocalDatabaseContent } from '../offline/userPanelConfigHelpers';
import { checkChromeModelsLocalDatabaseContent } from '../offline/chromeModelHelpers';
import { SELECTED_NOTEBOOK } from '../../constants';

// ---------------------------------------------------------------------------
// Shared helper: build notebooks context from a notebooks dict
// ---------------------------------------------------------------------------
const buildNotebooksContext = (notebooks) => {
  const combined = {
    ...notebooks,
    [SELECTED_NOTEBOOK.notebook_id]: SELECTED_NOTEBOOK,
  };
  let defaultNotebook = null;
  const notebooksIterable = Object.values(combined).map((value) => {
    if (value.default_notebook) defaultNotebook = value;
    return value;
  });
  return { notebooks: combined, notebooksIterable, defaultNotebook };
};

// ---------------------------------------------------------------------------
// Shared helper: build chromeModels array from cloud response object
// ---------------------------------------------------------------------------
const buildChromeModelsArray = (data) => {
  if (!data || typeof data !== 'object') return [];
  return Object.keys(data).map((key) => ({ modelId: key, model: data[key] }));
};

// ---------------------------------------------------------------------------
// Entity state machine factory
// Each entity collapses to: FETCH → READY | ERROR
// ---------------------------------------------------------------------------
const makeEntityState = (fetchSrc, onDoneAssign, onErrorAssign) => ({
  initial: 'FETCH',
  states: {
    FETCH: {
      invoke: {
        src: fetchSrc,
        onDone: {
          target: 'READY',
          actions: assign(onDoneAssign),
        },
        onError: {
          target: 'ERROR',
          actions: assign(onErrorAssign),
        },
      },
    },
    READY: { type: 'final' },
    ERROR: { type: 'final' },
  },
});

// ---------------------------------------------------------------------------
// Required app data parallel state
// ---------------------------------------------------------------------------
const requiredAppDataState = {
  id: 'fetchRequiredApplicationData',
  type: 'parallel',
  states: {

    // -------------------------------------------------------------------------
    // userSettings
    // -------------------------------------------------------------------------
    userSettings: makeEntityState(
      createOfflineFirstService({
        entityName: 'userSettings',
        checkLocalDB: checkUserSettingsLocalDatabaseContent,
        hasLocalData: (r) => r.hasUserSettingsLocalDatabaseContent,
        fetchFromCloud: fetchUserSettingsFromCloud,
      }),
      (context, event) => {
        logger.info({ source: event.data.source }, 'userSettings onDone');
        const { source, localData, cloudData } = event.data;
        if (source === 'local') {
          // localData.userSettings is now populated by checkUserSettingsLocalDatabaseContent.
          // Using it here avoids a cloud fetch on every refresh with cached data.
          // Without this, context.userSettings stayed null for the local path, which caused
          // setSelectAreaOfFocus to fail (no selectedAreaOfFocusId) and primeAreaOfFocus
          // to hang indefinitely in setAreaOfFocus.
          return {
            userSettings: localData?.userSettings ?? null,
            hasUserSettings: true,
            hasUserSettingsLocalDatabaseContent: true,
          };
        }
        return { userSettings: cloudData ?? {}, hasUserSettings: true };
      },
      () => ({
        errorPageMessage: 'failedToFetchLocalDBContent for user settings',
        hasError: true,
        hasUserSettings: true,
        userSettings: {},
      }),
    ),

    // -------------------------------------------------------------------------
    // areasOfFocusCollections
    // -------------------------------------------------------------------------
    areasOfFocusCollections: makeEntityState(
      createOfflineFirstService({
        entityName: 'areasOfFocusCollections',
        checkLocalDB: checkAreasOfFocusCollectionsLocalDatabaseContent,
        hasLocalData: (r) => r.hasAreasOfFocusCollectionsLocalDatabaseContent,
        fetchFromCloud: fetchAreasOfFocusCollectionsFromCloud,
      }),
      (context, event) => {
        logger.info({ source: event.data.source }, 'areasOfFocusCollections onDone');
        const { source, localData, cloudData } = event.data;
        if (source === 'local') {
          const aoc = localData?.areasOfFocusCollections ?? {};
          return {
            areasOfFocusCollections: aoc,
            areasOfFocusCollectionsIterable: Object.values(aoc),
            hasAreasOfFocusCollections: true,
            hasAreasOfFocusCollectionsLocalDatabaseContent: true,
          };
        }
        // cloudData is an array
        const aofList = cloudData ?? [];
        const aofDictionary = aofList.reduce((obj, item) => {
          obj[item.area_of_focus_collection_id] = item;
          return obj;
        }, {});
        return {
          areasOfFocusCollectionsIterable: aofList,
          areasOfFocusCollections: aofDictionary,
          hasAreasOfFocusCollections: true,
        };
      },
      (context) => ({
        errorPageMessage: 'areasOfFocusCollections failedToFetchLocalDBContent',
        hasError: true,
        hasAreasOfFocusCollections: true,
        areasOfFocusCollections: context.areasOfFocusCollections || {},
        areasOfFocusCollectionsIterable: context.areasOfFocusCollectionsIterable || [],
      }),
    ),

    // -------------------------------------------------------------------------
    // notes
    // -------------------------------------------------------------------------
    notes: makeEntityState(
      createOfflineFirstService({
        entityName: 'notes',
        checkLocalDB: checkNotesLocalDatabaseContent,
        hasLocalData: (r) => r.hasNotesLocalDatabaseContent,
        fetchFromCloud: fetchNotesFromCloudPaginated,
      }),
      (context, event) => {
        logger.info({ source: event.data.source }, 'notes onDone');
        const { source, localData, cloudData } = event.data;
        if (source === 'local') {
          return {
            notes: localData?.notes ?? {},
            hasNotes: true,
            hasNotesLocalDatabaseContent: true,
          };
        }
        return { notes: cloudData ?? {}, hasNotes: true };
      },
      (context) => ({
        errorPageMessage: 'failedToFetchLocalDBContent for notes',
        hasError: true,
        hasNotes: true,
        notes: context.notes || {},
      }),
    ),

    // -------------------------------------------------------------------------
    // notebooks
    // -------------------------------------------------------------------------
    notebooks: makeEntityState(
      createOfflineFirstService({
        entityName: 'notebooks',
        checkLocalDB: checkNotebooksLocalDatabaseContent,
        hasLocalData: (r) => r.hasNotebooksLocalDatabaseContent,
        fetchFromCloud: fetchNotebooksFromCloud,
      }),
      (context, event) => {
        logger.info({ source: event.data.source }, 'notebooks onDone');
        const { source, localData, cloudData } = event.data;
        const raw = source === 'local' ? (localData?.notebooks ?? {}) : (cloudData ?? {});
        const { notebooks, notebooksIterable, defaultNotebook } = buildNotebooksContext(raw);
        return {
          notebooks,
          notebooksIterable,
          defaultNotebook,
          hasNotebooks: true,
          hasNotebooksLocalDatabaseContent: source === 'local',
        };
      },
      (context) => ({
        errorPageMessage: 'failedToFetchLocalDBContent for notebooks',
        hasError: true,
        hasNotebooks: true,
        notebooks: context.notebooks || {},
        notebooksIterable: context.notebooksIterable || [],
      }),
    ),

    // -------------------------------------------------------------------------
    // userPanelConfigs
    // -------------------------------------------------------------------------
    userPanelConfigs: makeEntityState(
      createOfflineFirstService({
        entityName: 'userPanelConfigs',
        checkLocalDB: checkUserPanelConfigsLocalDatabaseContent,
        hasLocalData: (r) => r.hasUserPanelConfigsLocalDatabaseContent,
        fetchFromCloud: fetchUserPanelConfigsFromCloud,
      }),
      (context, event) => {
        logger.info({ source: event.data.source }, 'userPanelConfigs onDone');
        const { source, localData, cloudData } = event.data;
        if (source === 'local') {
          // localData.userPanelConfigs is now populated by checkUserPanelConfigsLocalDatabaseContent.
          // Using it here avoids a cloud fetch on every refresh with cached data.
          return {
            userPanelConfigs: localData?.userPanelConfigs ?? null,
            hasUserPanelConfigs: true,
            hasUserPanelConfigsLocalDatabaseContent: true,
          };
        }
        return { hasUserPanelConfigs: true, userPanelConfigs: cloudData };
      },
      (context) => ({
        errorPageMessage: 'failedToFetchLocalDBContent for user panel configs',
        hasError: true,
        hasUserPanelConfigs: true,
        userPanelConfigs: context.userPanelConfigs || {},
      }),
    ),

    // -------------------------------------------------------------------------
    // chromeModels
    // -------------------------------------------------------------------------
    chromeModels: makeEntityState(
      createOfflineFirstService({
        entityName: 'chromeModels',
        checkLocalDB: checkChromeModelsLocalDatabaseContent,
        hasLocalData: (r) => Boolean(r?.models?.length),
        fetchFromCloud: fetchChromeModelsFromCloud,
      }),
      (context, event) => {
        logger.info({ source: event.data.source }, 'chromeModels onDone');
        const { source, localData, cloudData } = event.data;
        if (source === 'local') {
          return {
            chromeModels: localData?.models ?? [],
            hasChromeModels: true,
            hasChromeModelsLocalDatabaseContent: true,
          };
        }
        return { chromeModels: buildChromeModelsArray(cloudData), hasChromeModels: true };
      },
      (context) => ({
        errorPageMessage: 'failedToFetchChromeModelsLocalDBContent',
        hasError: true,
        hasChromeModels: true,
        chromeModels: context.chromeModels || [],
      }),
    ),
  },

  onDone: {
    target: 'validateRequiredAppData',
    actions: assign((context, event) => {
      logger.info('validateRequiredAppData READY data', context, event);
      return {};
    }),
  },
};

export default requiredAppDataState;
